import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  introDataApi,
  type ClearableIntroSection,
  type ClearIntroSectionResult,
  type ClearIntroSetResult,
  type DeleteIntroCertificateResult,
  type IntroTrigger,
  type MaterializeIntroSetResult,
} from 'services'

/**
 * The `intro` sample dataset (VRG-512).
 *
 * ── The tri-state matters, and callers must respect it ──────────────────────────────
 * `hasIntroData` is `boolean | undefined`, NOT `boolean`. `undefined` means "not known
 * yet", and it is deliberately distinct from `false`.
 *
 * Collapse the two and the Analytics banner flickers on every page load: the query
 * starts un-resolved, `false` renders no banner, the response arrives, the banner pops
 * in and shoves the page down. Worse, the "clear sample data" affordance would appear
 * to be absent for a moment on a tenant that does have samples.
 *
 * `ProductTourProvider`'s `SeenState` uses the same tri-state for the same reason —
 * treating "unknown" as "unseen" there would replay a finished tour on every load.
 */
const INTRO_QUERY_KEY = ['intro-data', 'status'] as const

export interface UseIntroDataResult {
  /** `undefined` until the query settles. Never coerce this to a boolean. */
  hasIntroData: boolean | undefined
  /**
   * The tenant is ELIGIBLE for sample data — it has no real products yet.
   * `undefined` until settled, same contract as `hasIntroData`.
   *
   * NOT the same as `!hasIntroData`. A tenant with real data holds no samples AND
   * cannot receive any, so an affordance keyed on `!hasIntroData` offers an action that
   * silently no-ops. That shipped, and was caught by clicking the button in a browser.
   */
  canSeed: boolean | undefined
  /**
   * Has a REAL certificate shown up for this tenant yet (VRG-512)? `undefined` until
   * settled, same tri-state contract as the other two fields — see
   * `IntroDataStatus.hasRealData` for what this drives and why it is independent of
   * `hasIntroData` (samples can still be present, uncleared, after this flips true).
   */
  hasRealData: boolean | undefined
  /**
   * Has this tenant EVER cleared its intro sample set (VRG-512)? `undefined` until
   * settled, same tri-state contract as the other three.
   *
   * The tri-state matters more here than anywhere else in this hook, because the two
   * settled values drive DIFFERENT LAYOUTS rather than different copy: `false` gives
   * Home's sample-data card its full expanded form, `true` gives it a collapsed
   * accordion. Read `undefined` as `false` and a tenant who just cleared gets the full
   * card for one frame and then watches it collapse — a layout shift manufactured out
   * of a loading state, which is exactly what §19 exists to prevent.
   *
   * See `IntroDataStatus.hasCleared` for why this is server-recorded rather than a
   * client flag.
   */
  hasCleared: boolean | undefined
  /**
   * ISO timestamp of the oldest sample row this tenant holds (VRG-512 v10).
   *
   * ⚠️ THREE-VALUED, and all three are distinct — this is the one field where collapsing
   * them changes a NUMBER rather than a layout. `undefined` = not settled;
   * `null` = settled, no samples; a string = settled, and this is how far the fabricated
   * history reaches back.
   *
   * `RoiHero` floors its all-time window at `min(company.created_at, this)`, so reading
   * `undefined` as `null` would fetch the narrow window first and then re-fetch the wide
   * one — the reader watches the headline multiple, Value delivered and the stated
   * "since" date all jump. `RoiHero` therefore holds its request until this settles;
   * see its `introLoading` gate and `IntroDataStatus.earliestEventAt` for why the client
   * cannot derive the value itself.
   */
  earliestEventAt: string | null | undefined
  /** True once the answer is known either way — the gate for rendering anything. */
  isReady: boolean
  isLoading: boolean
}

export const useIntroData = (): UseIntroDataResult => {
  const { data, isLoading, isSuccess } = useQuery({
    queryKey: INTRO_QUERY_KEY,
    queryFn: () => introDataApi.status(),
    // Sample data changes only when this user seeds or clears it, and both paths
    // invalidate explicitly below. Refetching on every window focus would be pure
    // noise on a page the user leaves open.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  return {
    hasIntroData: isSuccess ? data?.present : undefined,
    canSeed: isSuccess ? data?.canSeed : undefined,
    hasRealData: isSuccess ? data?.hasRealData : undefined,
    hasCleared: isSuccess ? data?.hasCleared : undefined,
    earliestEventAt: isSuccess ? data?.earliestEventAt ?? null : undefined,
    isReady: isSuccess,
    isLoading,
  }
}

/**
 * Seed the intro set.
 *
 * Idempotent server-side, so a caller may fire it without first checking whether a set
 * exists — the response's `created` flag says which happened, and `reason` says why not
 * when it did nothing.
 */
export const useMaterializeIntroData = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<MaterializeIntroSetResult, Error, IntroTrigger>({
    mutationFn: (trigger: IntroTrigger) => introDataApi.create(trigger),
    onSuccess: () => invalidateIntroSurfaces(queryClient),
  })

  return {
    materialize: mutation.mutate,
    materializeAsync: mutation.mutateAsync,
    isMaterializing: mutation.isPending,
    materializeError: mutation.error,
  }
}

/** Remove the whole intro set. Idempotent — reports zeroes rather than failing. */
export const useClearIntroData = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<ClearIntroSetResult, Error, void>({
    mutationFn: () => introDataApi.clear(),
    onSuccess: () => invalidateIntroSurfaces(queryClient),
  })

  return {
    clearIntroData: mutation.mutate,
    clearIntroDataAsync: mutation.mutateAsync,
    isClearing: mutation.isPending,
    clearError: mutation.error,
  }
}

/**
 * Remove ONE surface's samples, leaving the rest of the set in place (VRG-512).
 *
 * Invalidates exactly what the full clear does. That is not laziness: a section clear
 * changes the intro STATUS (`present` can flip false once the last section goes) and it
 * changes analytics, so under-invalidating would leave the banner and the KPI tiles
 * disagreeing with the list the user is looking at. Over-invalidating costs a refetch.
 *
 * ⚠️ NO FALLBACK TO `clear()` ON FAILURE. The server 400s for a section it cannot scope
 * honestly, and answering that by clearing everything is exactly the substitution the
 * refusal exists to prevent. Surface the error.
 */
export const useClearIntroSection = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<ClearIntroSectionResult, Error, ClearableIntroSection>({
    mutationFn: section => introDataApi.clearSection(section),
    onSuccess: () => invalidateIntroSurfaces(queryClient),
  })

  return {
    clearIntroSection: mutation.mutate,
    clearIntroSectionAsync: mutation.mutateAsync,
    isClearingSection: mutation.isPending,
    clearSectionError: mutation.error,
  }
}

/**
 * Delete ONE sample certificate — the per-row "Remove sample" kebab action (AC4).
 *
 * `deletingCertificateId` is `mutation.variables`, which react-query keeps set to the
 * last id passed to `mutate`/`mutateAsync` for the lifetime of that call (it does not
 * reset until the NEXT call). Combined with `isDeletingIntroCertificate` (`isPending`),
 * a caller can tell "this row's own delete is in flight" apart from "some OTHER row's
 * delete is in flight" — the distinction §46's category 3 (in-flight mutation) needs:
 * only the row actually being deleted should show `disabled`, not every sample row on
 * the page.
 */
export const useDeleteIntroCertificate = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<DeleteIntroCertificateResult, Error, string>({
    mutationFn: (certificateId: string) => introDataApi.deleteCertificate(certificateId),
    onSuccess: () => invalidateIntroSurfaces(queryClient),
  })

  return {
    deleteIntroCertificate: mutation.mutate,
    deleteIntroCertificateAsync: mutation.mutateAsync,
    isDeletingIntroCertificate: mutation.isPending,
    deletingCertificateId: mutation.isPending ? mutation.variables : undefined,
    deleteIntroCertificateError: mutation.error,
  }
}

/**
 * Seeding or clearing changes what EVERY list and aggregate in the console returns, so
 * a narrow invalidation of just the status key would leave the user staring at a
 * Products table that still shows three samples they had just deleted.
 *
 * Invalidating broadly is the right trade here: this fires at most a handful of times
 * in a tenant's life (once to seed, once to clear), so the cost of refetching is
 * irrelevant next to the cost of a stale surface.
 */
function invalidateIntroSurfaces(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: INTRO_QUERY_KEY })
  // Prefix-matched, so these catch every keyed variant (filters, pagination, ranges).
  for (const key of [['products'], ['certificates'], ['analytics'], ['owners'], ['scans']]) {
    queryClient.invalidateQueries({ queryKey: key })
  }
}
